<template>
	<slot v-if="slots.default && isLoadingDone" />
</template>

<script lang="ts" setup>
// import PRELOAD_FILES from "@/assets/preload-files.txt?raw";
import {
	recalcSegmentsForEventProduction,
	showIntroPopup,
	syncInGameCompensationRewards,
	tryShowIntroPopup,
	useEventStore,
} from "@/event";
import { persistEventCleanup } from "@/event/session/eventEndTeardownPersist";
import { flushPendingStarterBuildingsOnEventStart } from "@/event/session/eventStartBuildingCleanup";
import { createSegmentManager } from "@/hooks/useSegmentManager";
import { createSiegeSystemRepository } from "@/hooks/useSiegeSystemRepository";
import useSocketService from "@/hooks/useSocketService";
import { createTileRepository } from "@/hooks/useTileRepository";
import Communicator from "@/libs/AppCommunicatior";
import GameError, { GameErrorType } from "@/libs/GameError";
import { EmitStores } from "@/libs/SocketService";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore";
import { SkinTypes } from "@/types/ui";
import { initTranslations } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { ValiError } from "valibot";
import { markRaw, onMounted, useSlots, watch } from "vue";
import FightResultPopup from "./FightSystem/FightResultPopup.vue";

const CONSOLE_COLOR = [
	"%c ERROR ",
	"background-color:maroon;color:white;font-weight:700;",
];

const slots: ReturnType<typeof useSlots> = useSlots();

const socketService = markRaw(useSocketService());
const communicator = markRaw(Communicator.getInstance());

const { isActive } = storeToRefs(useEventStore());

const runtimeStore = useRuntimeStore();
const { setLoadingDone } = runtimeStore;
const { isLoadingDone } = storeToRefs(runtimeStore);

const handleEventStart = async (skipSync = false): Promise<void> => {
	if (!isActive.value) return;

	if (!skipSync) {
		await socketService.emitSaveRequest({
			stores: [EmitStores.MAP, EmitStores.BUILDINGS_STORAGE],
		});
		await socketService.emitLoadRequest({
			stores: [EmitStores.BUILDINGS_STORAGE, EmitStores.EVENT_OVERVIEW],
		});
		if (runtimeStore.isMaintenanceMode) {
			return;
		}
	}

	void tryShowIntroPopup();
	showIntroPopup();
};

watch(
	(): boolean => isActive.value,
	(newVal: boolean, oldVal: boolean): void => {
		if (newVal !== oldVal) {
			//TODO Toto sa ma spustat iba pri konci eventu
			//TODO prepocitat iba segmenty s eventovymi budovami
			recalcSegmentsForEventProduction();
		}
		if (newVal && !oldVal) {
			if (isLoadingDone.value) {
				handleEventStart();
			}
		}
	},
);

watch(
	(): boolean => isLoadingDone.value,
	(ready: boolean): void => {
		if (ready) {
			handleEventStart(true);
		}
	},
);

onMounted(async (): Promise<void> => {
	setLoadingDone(false);

	communicator.setLoadingProgress(0.1);

	// Musi sa pridat lebo takto to viem prerusit pre maintenance
	const loadData = async (): Promise<void> => {
		try {
			await socketService.connect();

			communicator.setLoadingProgress(0.2);

			// Load user data first to get the correct language preference
			const { tiles, claimableRewards } =
				await socketService.emitLoadRequest();

			if (runtimeStore.isMaintenanceMode) {
				communicator.loadingDone();
				return;
			}

			communicator.setLoadingProgress(0.4);

			// Initialize translations after user data is loaded with correct language
			await initTranslations();

			communicator.setLoadingProgress(0.6);

			const tilerepo = createTileRepository(tiles);
			createSegmentManager(tilerepo);
			createSiegeSystemRepository(tilerepo);

			if (flushPendingStarterBuildingsOnEventStart()) {
				useEventStore().setStarterMapCleaned(true);
				void persistEventCleanup();
			}

			try {
				//Spustenie asynchrónnej inicializácie RoadGraphu
				await tilerepo.initializeRoadGraph();
			} catch (err: unknown) {
				console.error("Failed to initialize RoadGraph from DB:", err);
				// Fallback ak zlyhá DB: prepočítať okamžite
				tilerepo.rebuildRoadGraph();
			}

			if (claimableRewards?.length)
				window.purchaseFinished("", "true", claimableRewards);

			syncInGameCompensationRewards();

			communicator.setLoadingProgress(0.7);

			setLoadingDone(true);

			if (useSiegeSystemStore().result) {
				usePopupStore().showPopup({
					type: PopupType.INFO,
					skin: SkinTypes.DARK,
					isPaddingless: true,
					isCentered: true,
					isPriority: true,
					height: "auto",
					width: 23.875,
					component: markRaw(FightResultPopup),
				});
			}
		} catch (error: unknown) {
			console.error(
				...CONSOLE_COLOR,
				"Error captured in data loader",
				error,
			);

			if (error instanceof ValiError)
				throw new GameError(
					GameErrorType.BE_VALIDATION_ERROR,
					error.message,
				);

			if (error instanceof GameError)
				throw new GameError(error.type, error.message);

			if (error instanceof Error) throw new Error(error.message);
		}
	};

	await loadData();
});
</script>

<style lang="scss" scoped></style>
